import { EMPTY_OBJECT, isNullish } from '../';

interface Options {
  isLooseOrder?: boolean;
  isStrictNaN?: boolean;
  isStrictZero?: boolean;
  nullishSensitivity?: 'strict' | 'nullish' | 'empty';
  stringSensitivity?: 'base' | 'accent' | 'case' | 'variant'; // see [MDN](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/Collator/Collator#sensitivity) for options
}

const INSTANCE_TYPES_WITH_ENTRIES = [Map, Set, FormData, URLSearchParams];
const NULL = 'null';

function findInstanceTypesWithEntries(v1: unknown, v2: unknown) {
  for (const instance of INSTANCE_TYPES_WITH_ENTRIES) {
    if (v1 instanceof instance && v2 instanceof instance) {
      return instance;
    }
  }

  return;
}

/**
 * Compare two objects for equality
 *
 * @param {any} value1 The first value to compare
 * @param {any} value2 The second value to compare
 * @param {object} options Booleans default to `false`;
 *
 * `stringSensitivity` examples (defaults to `'variant'`):
 * - `'base'`: a ≠ b, a = á, a = A
 * - `'accent'`: a ≠ b, a ≠ á, a = A
 * - `'case'`: a ≠ b, a = á, a ≠ A
 * - `'variant'`: a ≠ b, a ≠ á, a ≠ A
 *
 * `nullishSensitivity` examples (defaults to `'strict'`):
 * - `'strict'`: null ≠ undefined ≠ '' (empty string)
 * - `'nullish'`: null = undefined ≠ '' (empty string)
 * - `'empty'`: null = undefined = '' (empty string)
 * @returns {boolean} A boolean determining if the values are equal or not
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isEqual(value1: any, value2: any, options: Options = EMPTY_OBJECT): boolean {
  const {
    isLooseOrder,
    isStrictZero,
    isStrictNaN,
    stringSensitivity = 'variant',
    nullishSensitivity = 'strict',
  } = options;

  const nan = Number.isNaN(value1) || Number.isNaN(value2);

  // Object.is(NaN, NaN) -> true; NaN === NaN -> false
  if (isStrictNaN && nan) {
    return false;
  } else if (Object.is(value1, value2)) {
    return true;
  }

  // get actual value if Number, String, Boolean, Array, Date, etc. constructor
  const val1 = value1 && typeof value1 === 'object' ? value1.valueOf() : value1;
  const val2 = value2 && typeof value2 === 'object' ? value2.valueOf() : value2;

  // typeof null === 'object', so return 'null' instead
  const type1 = val1 === null ? NULL : typeof val1;
  const type2 = val2 === null ? NULL : typeof val2;

  const isVal1Nullish = isNullish(val1);
  const isVal2Nullish = isNullish(val2);
  const isVal1Empty = val1 === '';
  const isVal2Empty = val2 === '';

  if (nullishSensitivity === 'nullish' && isVal1Nullish && isVal2Nullish) {
    return true;
  } else if (
    nullishSensitivity === 'empty' &&
    (isVal1Nullish || isVal1Empty) &&
    (isVal2Nullish || isVal2Empty)
  ) {
    return true;
  } else if (type1 !== type2) {
    return false;
  }

  let instance;

  if (type1 === 'string') {
    return val1.localeCompare(val2, 'en-US', { sensitivity: stringSensitivity }) === 0;
  } else if (type1 === 'number' && isStrictZero) {
    return Object.is(val1, val2);
  } else if (type1 !== 'object') {
    return val1 === val2;
  } else if (Array.isArray(val1)) {
    const equal = val1.length === val2.length;
    let callback;

    if (isLooseOrder) {
      callback = (val1: unknown) => val2.find((val2: unknown) => isEqual(val2, val1, options));
    } else {
      callback = (val: unknown, index: number) => isEqual(val, val2[index], options);
    }

    return equal && val1.every(callback);
  } else if ((instance = findInstanceTypesWithEntries(val1, val2))) {
    const isSet = instance === Set;
    let equal;

    if ('size' in val1 && 'size' in val2) {
      equal = val1.size === val2.size;
    } else {
      equal =
        Object.keys(Object.fromEntries(val1)).length ===
        Object.keys(Object.fromEntries(val2)).length;
    }

    if (!equal) {
      return false;
    }

    if (!isLooseOrder) {
      const entries1 = val1.entries();
      const entries2 = val2.entries();

      for (const [key1, value1] of entries1) {
        const { done, value } = entries2.next();

        if (done) {
          // val1 is bigger than val2
          return false;
        }

        const [key2, value2] = value;

        // a Set's "keys" are just its value, so exclude key check and compare only values with options
        if ((!isSet && key1 !== key2) || !isEqual(value1, value2, options)) {
          return false;
        }
      }

      // val1 is smaller than val2
      const { done } = entries2.next();
      if (!done) {
        return false;
      }

      equal = true;
    } else if (!isSet) {
      const get = instance !== Map ? 'getAll' : 'get';
      val1.forEach((_: unknown, key: string, parent: typeof val1) => {
        const value = parent[get](key);
        if (!isEqual(value, val2[get](key), options)) {
          equal = false;
        }
      });
    } else {
      const val2Arr = Array.from(val2);
      val1.forEach((value: unknown) => {
        const match = val2Arr.some((value2) => isEqual(value, value2, options));
        if (!match) {
          equal = false;
        }
      });
    }

    return equal;
  }

  let equal = Object.keys(val1).length === Object.keys(val2).length;

  if (!equal) {
    return false;
  }

  for (const key in val1) {
    equal = isEqual(val1[key], val2[key], options);

    if (!equal) {
      break;
    }
  }

  return equal;
}
